/**
 * PaymentTermsForm — Onda 5a (redesign iOS).
 *
 * Componente reutilizável para capturar Condições de Pagamento.
 * Reaproveitado em Fornecedores, Despesas, Pagamentos e Contratações.
 *
 * Estrutura de `terms`:
 *   {
 *     type: 'avista' | 'aprazo',
 *     // À Vista:
 *     method?: 'pix'|'cartao_credito'|'cartao_debito'|'boleto'|'transferencia'|'dinheiro'|'cheque',
 *     payment_date?: 'YYYY-MM-DD',
 *     cheque_data?: { bank, number, date },
 *     // A Prazo:
 *     has_down_payment?: bool,
 *     down_payment?: { amount, date, method, cheque_data? },
 *     num_installments?: int,
 *     value_mode?: 'auto' | 'manual',
 *     default_installment_method?: string,   // governa o meio de TODAS as parcelas
 *     installments?: [{ value, due_date, method, cheque_data? }],
 *   }
 *
 * UX (decisão do usuário): o "Meio padrão" governa todas as parcelas — NÃO repetimos
 * o seletor de meio em cada parcela. Exceção: quando o meio padrão é "cheque",
 * cada parcela exibe os campos do cheque (banco/folha/compensação), pois cada cheque é único.
 */
import React, { useEffect, useState } from "react";

const METHODS = [
  { value: "pix",              label: "PIX" },
  { value: "cartao_credito",   label: "Cartão de Crédito" },
  { value: "cartao_debito",    label: "Cartão de Débito" },
  { value: "boleto",           label: "Boleto" },
  { value: "transferencia",    label: "Transferência" },
  { value: "dinheiro",         label: "Dinheiro" },
  { value: "cheque",           label: "Cheque" },
];
const PARCEL_METHODS = METHODS.filter((m) => ["pix", "boleto", "cartao_credito", "transferencia", "cheque"].includes(m.value));

const fmtBRL = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDateBR = (iso) => { if (!iso) return "Sem data"; const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; };

const addMonthsISO = (baseIso, monthsToAdd) => {
  if (!baseIso) return "";
  const [y, m, d] = baseIso.split("-").map(Number);
  const dt = new Date(y, (m - 1) + monthsToAdd, d || 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

/* ─── Tokens visuais estilo iOS (grouped inset lists) ─── */
const groupTitle = "px-1 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400";
const cardCls = "bg-white rounded-2xl ring-1 ring-black/[0.06] divide-y divide-zinc-100 overflow-hidden";
const rowCls = "flex items-center justify-between gap-3 px-4 min-h-[46px] py-2";
const rowLabel = "text-[14px] text-zinc-700 shrink-0";
const trailingInput = "bg-transparent text-[16px] sm:text-[14px] text-zinc-900 text-right focus:outline-none w-full placeholder:text-zinc-300";

const Chevron = () => (
  <svg className="pointer-events-none text-zinc-300 ml-0.5 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
);

function Row({ label, children }) {
  return (
    <div className={rowCls}>
      <span className={rowLabel}>{label}</span>
      <div className="flex-1 min-w-0 flex items-center justify-end">{children}</div>
    </div>
  );
}

function IOSSelect({ value, onChange, options, testid }) {
  return (
    <div className="flex items-center justify-end max-w-[60%]">
      <select
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="appearance-none bg-transparent text-[16px] sm:text-[14px] text-zinc-500 text-right focus:outline-none cursor-pointer truncate"
      >
        {options.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
      </select>
      <Chevron />
    </div>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <div className="flex p-1 rounded-2xl bg-zinc-200/60">
      {options.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={String(o.id)}
            type="button"
            onClick={() => onChange(o.id)}
            data-testid={o.testid}
            className={`flex-1 px-3 py-2 rounded-xl text-[13.5px] font-medium transition-all ${
              active ? "bg-white text-zinc-900 shadow-[0_1px_3px_rgba(0,0,0,0.12)]" : "text-zinc-500"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function PaymentTermsForm({ totalAmount = 0, value, onChange }) {
  const terms = value || { type: "avista", method: "pix" };
  const update = (patch) => onChange?.({ ...terms, ...patch });

  const downPaymentValue = terms.has_down_payment ? Number(terms.down_payment?.amount || 0) : 0;
  const remainingTotal = Math.max(0, Number(totalAmount || 0) - downPaymentValue);
  const defaultMethod = terms.default_installment_method || "boleto";
  const isCheque = defaultMethod === "cheque";

  // ─── Auto-recalcula parcelas (preserva cheque_data por índice) ───
  useEffect(() => {
    if (terms.type !== "aprazo") return;
    if (terms.value_mode === "manual") return;
    const num = Math.max(1, Number(terms.num_installments || 2));
    const baseValue = Math.round((remainingTotal / num) * 100) / 100;
    const baseDate = terms.first_installment_date || addMonthsISO(new Date().toISOString().slice(0, 10), 1);
    const prev = terms.installments || [];
    const list = Array.from({ length: num }, (_, i) => ({
      value: baseValue,
      due_date: addMonthsISO(baseDate, i),
      method: defaultMethod,
      ...(prev[i]?.cheque_data ? { cheque_data: prev[i].cheque_data } : {}),
    }));
    const same = JSON.stringify(list.map((l) => [l.value, l.due_date, l.method])) ===
                 JSON.stringify(prev.map((l) => [l.value, l.due_date, l.method]));
    if (!same) onChange?.({ ...terms, installments: list });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terms.type, terms.value_mode, terms.num_installments, terms.first_installment_date,
      terms.default_installment_method, remainingTotal, terms.has_down_payment]);

  const manualMode = terms.value_mode === "manual";

  return (
    <div className="space-y-5 bg-[#F2F2F7] rounded-2xl p-3 sm:p-3.5" data-testid="payment-terms-form">
      {/* Tipo de pagamento */}
      <Segmented
        options={[
          { id: "avista", label: "À Vista", testid: "pt-type-avista" },
          { id: "aprazo", label: "A Prazo", testid: "pt-type-aprazo" },
        ]}
        value={terms.type}
        onChange={(id) => update({ type: id })}
      />

      {/* À Vista */}
      {terms.type === "avista" && (
        <div>
          <p className={groupTitle}>Pagamento à vista</p>
          <div className={cardCls}>
            <Row label="Valor">
              <span className="text-[15px] font-semibold text-zinc-900 tabular-nums">{fmtBRL(totalAmount)}</span>
            </Row>
            <Row label="Meio">
              <IOSSelect value={terms.method || "pix"} onChange={(e) => update({ method: e.target.value })} options={METHODS} testid="pt-avista-method" />
            </Row>
            <Row label="Data">
              <input type="date" value={terms.payment_date || ""} onChange={(e) => update({ payment_date: e.target.value })} className={trailingInput} data-testid="pt-avista-date" />
            </Row>
          </div>
          {terms.method === "cheque" && (
            <ChequeCard value={terms.cheque_data || {}} onChange={(d) => update({ cheque_data: d })} prefix="avista" />
          )}
        </div>
      )}

      {/* A Prazo */}
      {terms.type === "aprazo" && (
        <>
          {/* Entrada */}
          <div>
            <p className={groupTitle}>Entrada</p>
            <div className={cardCls}>
              <Row label="Possui entrada?">
                <div className="w-[150px]">
                  <Segmented
                    options={[
                      { id: true, label: "Sim", testid: "pt-entry-yes" },
                      { id: false, label: "Não", testid: "pt-entry-no" },
                    ]}
                    value={terms.has_down_payment || false}
                    onChange={(id) => update({ has_down_payment: id })}
                  />
                </div>
              </Row>
              {terms.has_down_payment && (
                <>
                  <Row label="Valor">
                    <input type="number" step="0.01" min={0} inputMode="decimal" placeholder="0,00" value={terms.down_payment?.amount ?? ""}
                      onChange={(e) => update({ down_payment: { ...(terms.down_payment || {}), amount: Number(e.target.value) || 0 } })}
                      className={trailingInput} data-testid="pt-entry-amount" />
                  </Row>
                  <Row label="Data">
                    <input type="date" value={terms.down_payment?.date || ""}
                      onChange={(e) => update({ down_payment: { ...(terms.down_payment || {}), date: e.target.value } })}
                      className={trailingInput} data-testid="pt-entry-date" />
                  </Row>
                  <Row label="Meio">
                    <IOSSelect value={terms.down_payment?.method || "pix"}
                      onChange={(e) => update({ down_payment: { ...(terms.down_payment || {}), method: e.target.value } })}
                      options={METHODS} testid="pt-entry-method" />
                  </Row>
                </>
              )}
            </div>
            {terms.has_down_payment && terms.down_payment?.method === "cheque" && (
              <ChequeCard
                value={terms.down_payment?.cheque_data || {}}
                onChange={(d) => update({ down_payment: { ...(terms.down_payment || {}), cheque_data: d } })}
                prefix="entry"
              />
            )}
          </div>

          {/* Parcelamento */}
          <div>
            <p className={groupTitle}>Parcelamento</p>
            <div className={cardCls}>
              <Row label="Parcelas">
                <Stepper
                  value={terms.num_installments || 2}
                  min={1}
                  max={60}
                  onChange={(v) => update({ num_installments: v })}
                />
              </Row>
              <div className="px-4 py-2.5 flex flex-wrap gap-2">
                {[1, 2, 3, 6, 12].map((q) => {
                  const active = (terms.num_installments || 2) === q;
                  return (
                    <button
                      key={q}
                      type="button"
                      onClick={() => update({ num_installments: q })}
                      data-testid={`pt-quick-${q}`}
                      className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold ring-1 transition-all active:scale-95 ${
                        active ? "bg-zinc-900 text-white ring-zinc-900" : "bg-white text-zinc-600 ring-zinc-200"
                      }`}
                    >
                      {q}x
                    </button>
                  );
                })}
              </div>
              <Row label="1º vencimento">
                <input type="date" value={terms.first_installment_date || ""}
                  onChange={(e) => update({ first_installment_date: e.target.value })}
                  className={trailingInput} data-testid="pt-first-due" />
              </Row>
              <Row label="Cálculo">
                <IOSSelect value={terms.value_mode || "auto"} onChange={(e) => update({ value_mode: e.target.value })}
                  options={[{ value: "auto", label: "Automático" }, { value: "manual", label: "Manual" }]} testid="pt-value-mode" />
              </Row>
              <Row label="Meio padrão">
                <IOSSelect value={defaultMethod}
                  onChange={(e) => {
                    const dm = e.target.value;
                    const next = (terms.installments || []).map((it) => ({ ...it, method: dm }));
                    update({ default_installment_method: dm, installments: next });
                  }}
                  options={PARCEL_METHODS} testid="pt-default-method" />
              </Row>
            </div>
            <p className="px-1 mt-1.5 text-[12px] text-zinc-500">
              Restante a parcelar: <strong className="text-zinc-700">{fmtBRL(remainingTotal)}</strong>
            </p>
          </div>

          {/* Cronograma */}
          {(terms.installments || []).length > 0 && (
            <div data-testid="pt-schedule">
              <p className={groupTitle}>Cronograma {METHODS.find((m) => m.value === defaultMethod)?.label && `· ${METHODS.find((m) => m.value === defaultMethod).label}`}</p>
              <div className={cardCls}>
                {(terms.installments || []).map((inst, idx) => (
                  <ParcelRow
                    key={idx}
                    idx={idx}
                    inst={inst}
                    manualMode={manualMode}
                    showCheque={isCheque}
                    onChange={(patch) => {
                      const next = [...(terms.installments || [])];
                      next[idx] = { ...next[idx], ...patch, method: defaultMethod };
                      update({ installments: next });
                    }}
                  />
                ))}
              </div>
              <p className="px-1 mt-1.5 text-[11.5px] text-zinc-400">
                Ao salvar, esses lançamentos aparecem no <strong className="text-zinc-600 font-medium">Financeiro</strong> como a receber/pagar.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* Card de cheque (estilo iOS) — banco / folha / compensação */
function Stepper({ value, min = 1, max = 60, onChange }) {
  const v = Math.max(min, Math.min(max, Number(value) || min));
  const btn = "w-9 h-9 rounded-full bg-zinc-100 text-zinc-800 text-[20px] leading-none font-medium flex items-center justify-center active:scale-90 transition-transform disabled:opacity-40 disabled:active:scale-100";
  return (
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => onChange(Math.max(min, v - 1))} disabled={v <= min}
        className={btn} data-testid="pt-installments-dec" aria-label="Diminuir parcelas">−</button>
      <span className="min-w-[44px] text-center text-[16px] font-semibold text-zinc-900 tabular-nums" data-testid="pt-num-installments">{v}x</span>
      <button type="button" onClick={() => onChange(Math.min(max, v + 1))} disabled={v >= max}
        className={btn} data-testid="pt-installments-inc" aria-label="Aumentar parcelas">+</button>
    </div>
  );
}

function ChequeCard({ value, onChange, prefix }) {
  return (
    <div className={`${cardCls} mt-2`}>
      <Row label="Banco">
        <input type="text" value={value.bank || ""} onChange={(e) => onChange({ ...value, bank: e.target.value })} className={trailingInput} placeholder="—" data-testid={`pt-${prefix}-cheque-bank`} />
      </Row>
      <Row label="Folha / Nº">
        <input type="text" value={value.number || ""} onChange={(e) => onChange({ ...value, number: e.target.value })} className={trailingInput} placeholder="—" data-testid={`pt-${prefix}-cheque-number`} />
      </Row>
      <Row label="Compensação">
        <input type="date" value={value.date || ""} onChange={(e) => onChange({ ...value, date: e.target.value })} className={trailingInput} data-testid={`pt-${prefix}-cheque-date`} />
      </Row>
    </div>
  );
}

function ParcelRow({ idx, inst, manualMode, showCheque, onChange }) {
  return (
    <div data-testid={`pt-parcel-${idx}`}>
      {manualMode ? (
        <>
          <div className="flex items-center gap-2.5 px-4 pt-2.5 pb-1">
            <span className="w-6 h-6 rounded-full bg-zinc-100 text-zinc-500 text-[11px] font-semibold flex items-center justify-center">{idx + 1}</span>
            <span className="text-[12px] font-medium text-zinc-400">Parcela {idx + 1}</span>
          </div>
          <Row label="Valor">
            <input type="number" step="0.01" min={0} inputMode="decimal" value={inst.value ?? ""}
              onChange={(e) => onChange({ value: Number(e.target.value) || 0 })}
              className={trailingInput} placeholder="0,00" />
          </Row>
          <Row label="Vencimento">
            <input type="date" value={inst.due_date || ""} onChange={(e) => onChange({ due_date: e.target.value })} className={trailingInput} />
          </Row>
        </>
      ) : (
        <div className="flex items-center justify-between gap-3 px-4 min-h-[52px] py-2.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-6 h-6 rounded-full bg-zinc-100 text-zinc-500 text-[11px] font-semibold flex items-center justify-center shrink-0">{idx + 1}</span>
            <div className="min-w-0 leading-tight">
              <p className="text-[14px] text-zinc-800">Parcela {idx + 1}</p>
              <p className="text-[12px] text-zinc-400">{fmtDateBR(inst.due_date)}</p>
            </div>
          </div>
          <span className="text-[15px] font-semibold text-zinc-900 tabular-nums shrink-0">{fmtBRL(inst.value)}</span>
        </div>
      )}
      {showCheque && (
        <div className="px-4 pb-3 pt-1">
          <div className={cardCls}>
            <Row label="Banco">
              <input type="text" value={inst.cheque_data?.bank || ""} onChange={(e) => onChange({ cheque_data: { ...(inst.cheque_data || {}), bank: e.target.value } })} className={trailingInput} placeholder="—" data-testid={`pt-p${idx}-cheque-bank`} />
            </Row>
            <Row label="Folha / Nº">
              <input type="text" value={inst.cheque_data?.number || ""} onChange={(e) => onChange({ cheque_data: { ...(inst.cheque_data || {}), number: e.target.value } })} className={trailingInput} placeholder="—" data-testid={`pt-p${idx}-cheque-number`} />
            </Row>
            <Row label="Compensação">
              <input type="date" value={inst.cheque_data?.date || ""} onChange={(e) => onChange({ cheque_data: { ...(inst.cheque_data || {}), date: e.target.value } })} className={trailingInput} data-testid={`pt-p${idx}-cheque-date`} />
            </Row>
          </div>
        </div>
      )}
    </div>
  );
}
